import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { strings } from 'helpers'
import { PROFILE_CONTAINER } from 'components/AuthModal/styles'
import { useAuthState, useUpdateUser } from 'hooks'
import { valueOrEmptyString } from 'helpers/strings'
import { USER_DATA_FIELDS, USER_DATA_KEYS } from 'constants/user-data'

import CloseButton from 'components/AuthModal/components/CloseButton'
import ProgressBar from 'components/AuthModal/components/ProgressBar'
import Button from 'components/AuthModal/components/Button'
import Label from 'components/AuthModal/components/Label'
import Select from 'components/AuthModal/components/Select'
import MotionContainer from 'components/AuthModal/components/MotionContainer'
import Input from 'components/AuthModal/components/Input'
import TimesThinIcon from 'components/TimesThinIcon'
import MultiSelect from 'components/AuthModal/components/MultiSelect'
import RangeSlider from 'components/AuthModal/components/RangeSlider'

const TITLES = {
  [USER_DATA_KEYS.PROFILE]: 'About You',
  [USER_DATA_KEYS.PREFERENCES]: 'What are you looking for?',
}

const getInitialData = ({ dataKey, user }) => {
  const initialData = {
    ...(user?.[dataKey] || {}),
  }
  const fields = USER_DATA_FIELDS[dataKey]
  fields.forEach(field => {
    if (field.type === 'range') {
      initialData[field.key] = {
        min:
          (field.normalizer
            ? field.normalizer(initialData[field.minField])
            : initialData[field.minField]) || field.start,
        max:
          (field.normalizer
            ? field.normalizer(initialData[field.maxField])
            : initialData[field.maxField]) || field.end,
      }
    }
  })

  return initialData
}

const UserDataStep = ({ onClose, onSubmit, autoFocus, data }) => {
  const { dataKey } = data
  const fields = USER_DATA_FIELDS[dataKey]
  const { update } = useUpdateUser()
  const submitButtonRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { user: loggedInUser } = useAuthState()
  const [profileData, setProfileData] = useState(getInitialData({ user: loggedInUser, dataKey }))
  const [currentKey, setCurrentKey] = useState(fields[0].key)

  useEffect(() => {
    if (Object.keys(profileData).length === 0) {
      setProfileData(getInitialData({ user: loggedInUser, dataKey }))
    }
  }, [loggedInUser, profileData, dataKey])

  const { currentField, currentValue, currentOptions } = useMemo(() => {
    const currentField = fields.find(field => field.key === currentKey)

    return {
      currentField,
      currentValue: profileData[currentKey],
      currentOptions: currentField?.dependsOn?.options
        ? currentField.dependsOn.options[profileData[currentField.dependsOn.key]?.toLowerCase()] ||
          []
        : currentField?.options || [],
    }
  }, [fields, currentKey, profileData])

  const { currentFieldIndex, isLastStep, isFirstStep, progress } = useMemo(() => {
    const currentFieldIndex = fields.findIndex(field => field === currentField)

    return {
      currentFieldIndex,
      isLastStep: currentFieldIndex === fields.length - 1,
      isFirstStep: currentFieldIndex === 0,
      progress: ((currentFieldIndex + 1) / fields.length) * 100,
    }
  }, [fields, currentField])

  const shouldSkipNextField = useCallback(
    (direction = 1) => {
      if (isLastStep) {
        return false
      }

      const nextField = fields[currentFieldIndex + direction]
      if (!nextField?.dependsOn) {
        return false
      }

      const dependentValue = profileData[nextField.dependsOn.key]
      const dependentField = fields.find(field => field.key === nextField.dependsOn.key)

      return (
        !dependentValue ||
        (dependentField.nullValues && dependentField.nullValues.includes(dependentValue))
      )
    },
    [profileData, isLastStep, currentFieldIndex, fields]
  )

  const goToStep = useCallback(
    direction => {
      const nextStepIndex = currentFieldIndex + direction
      setCurrentKey(fields[nextStepIndex].key)
    },
    [currentFieldIndex, fields]
  )

  const closeButton = useMemo(() => {
    const isVisible = true
    const Icon = isFirstStep ? TimesThinIcon : undefined
    const skipNextField = shouldSkipNextField(-1)

    return {
      isVisible,
      Icon,
      onClose: isFirstStep ? onClose : () => goToStep(skipNextField ? -2 : -1),
    }
  }, [onClose, goToStep, isFirstStep, shouldSkipNextField])

  const submitButton = useMemo(() => {
    const isEmpty = !currentValue || currentValue.length === 0
    const isDisabled = currentField.required && isEmpty
    const text =
      isEmpty && !currentField.required && dataKey === USER_DATA_KEYS.PREFERENCES
        ? 'Open to All'
        : 'Next'
    return {
      isDisabled,
      text,
      type: 'button',
    }
  }, [currentField, currentValue, dataKey])

  const handleChange = useCallback(
    e =>
      setProfileData({
        ...profileData,
        [e.target.name]: e.target.value,
      }),
    [profileData]
  )

  const handleRangeChange = useCallback(
    ({ key, values }) =>
      handleChange({
        target: {
          name: key,
          value: values,
        },
      }),
    [handleChange]
  )

  const handleSave = useCallback(
    async e => {
      e.preventDefault()
      if (submitButton.isDisabled) {
        return
      }

      setIsSubmitting(true)
      try {
        const currentField = fields[currentFieldIndex]
        const updateData =
          currentField.type === 'range'
            ? {
                [currentField.minField]: currentField.transformer
                  ? currentField.transformer(currentValue.min)
                  : currentValue.min,
                [currentField.maxField]: currentField.transformer
                  ? currentField.transformer(currentValue.max)
                  : currentValue.max,
              }
            : {
                [currentKey]: currentValue || null,
              }

        await update({
          [dataKey]: updateData,
          lastUpdateSource: 'webapp',
        })
        if (isLastStep) {
          onSubmit()
          return
        }

        const skipNextField = shouldSkipNextField()
        if (!skipNextField) {
          goToStep(1)
          return
        }

        // end chain if next field should be skipped and it's the second to last field
        if (currentFieldIndex === fields.length - 2) {
          onSubmit()
          return
        }

        goToStep(2)
      } catch (error) {
        console.error(error)
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      dataKey,
      currentKey,
      currentValue,
      isLastStep,
      shouldSkipNextField,
      currentFieldIndex,
      fields,
      goToStep,
      update,
      onSubmit,
      submitButton.isDisabled,
    ]
  )

  return (
    <>
      <form
        noValidate
        className={classnames(PROFILE_CONTAINER, 'justify-between')}
        onSubmit={handleSave}
      >
        {closeButton.isVisible && (
          <CloseButton onClose={closeButton.onClose} Icon={closeButton.Icon} />
        )}
        <div className="absolute top-4 text-white text-lg font-medium">{TITLES[data?.dataKey]}</div>
        <div className="w-full">
          <div className="mb-6">
            <ProgressBar progress={progress} />
          </div>
          <MotionContainer key={currentKey}>
            <div>
              <Label>{currentField.label}</Label>
              {currentField.type === 'select' && (
                <Select
                  name={currentField.key}
                  defaultLabel="Select One"
                  options={currentOptions}
                  onChange={handleChange}
                  value={strings.valueOrEmptyString(currentValue)}
                />
              )}
              {currentField.type === 'multi-select' && (
                <MultiSelect
                  name={currentField.key}
                  options={currentOptions}
                  onChange={handleChange}
                  value={currentValue}
                />
              )}
              {currentField.type === 'text' && (
                <Input
                  key={autoFocus}
                  autoFocus={autoFocus}
                  name={currentField.key}
                  value={valueOrEmptyString(currentValue)}
                  type="text"
                  placeholder={currentField.label}
                  onChange={handleChange}
                  required={currentField.required}
                />
              )}
              {currentField.type === 'range' && (
                <RangeSlider
                  key={autoFocus}
                  className="bg-woodsmokeBlack text-white"
                  autoFocus={autoFocus}
                  name={currentField.key}
                  value={currentValue}
                  start={currentField.start}
                  end={currentField.end}
                  transformer={currentField.transformer}
                  onChange={values => {
                    handleRangeChange({ values, key: currentField.key })
                  }}
                />
              )}
            </div>
          </MotionContainer>
        </div>
        <div className="flex flex-col w-full items-center">
          <Button
            type={submitButton.type}
            className="order-1 w-full mt-6 mx-auto text-base sm:order-none"
            disabled={submitButton.isDisabled}
            loading={isSubmitting}
            ref={submitButtonRef}
            onClick={handleSave}
          >
            {submitButton.text}
          </Button>
        </div>
      </form>
    </>
  )
}

UserDataStep.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  autoFocus: PropTypes.bool,
  user: PropTypes.object,
}

export default UserDataStep
